/* eslint-disable no-underscore-dangle */
import { Box, Button, Container, Divider, Icon } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowIcon from '@/assets/icons/ic-arrow.svg';

import {
  CaseStudyHeader,
  CaseStudyHeaderFallback,
} from '@/components/ui/features/case-studies/CaseStudyHeader/CaseStudyHeader';
import { GradientBanner } from '@/components/ui/shared/banners/GradientBanner/GradientBanner';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { ArticleMarkdown } from '@/components/ui/shared/markdowns/ArticleMarkdown/ArticleMarkdown';
import { ArticleSocialShare } from '@/components/ui/shared/social/ArticleSocialShare/ArticleSocialShare';
import { AccessFrom } from '@/enums/AccessFrom';
import { useServerSideResponse } from '@/hooks/useServerSideResponse';
import { IResponse } from '@/interfaces/json-api';
import { CaseStudy } from '@/resources/CaseStudy';
import { generateStore } from '@/utils/generateStore';
import { splitIdAndSlug } from '@/utils/helperFunctions';

import { fetchAllCaseStudies, fetchCaseStudy } from '../../fetchers/case-studies';

interface ICaseStudyPageProps {
  rawCaseStudy: IResponse;
}

const CaseStudyPage: NextPage<ICaseStudyPageProps> = ({ rawCaseStudy }) => {
  const router = useRouter();
  const { t } = useTranslation();

  const caseStudyResponse = useServerSideResponse<CaseStudy>(rawCaseStudy) as Response<CaseStudy>;

  if (router.isFallback) {
    return (
      <Layout>
        <Box h={['104px', null, '70px']} bg="black" mt={['-104px', null, '-70px']} />
        <Container>
          <CaseStudyHeaderFallback />
        </Container>
      </Layout>
    );
  }

  const caseStudy = caseStudyResponse.data as CaseStudy;

  return (
    <Fragment>
      <NextSeo
        title={caseStudy.title}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_BASE_URL}case-studies/${caseStudy?.linkSlug}`,
          title: caseStudy.title,
          description: caseStudy.shortDescription, // TODO: remove markdown
          images: [
            {
              url: caseStudy.mediumFeaturedImageUrl,
              width: 800,
              height: 600,
              alt: `Photo - ${caseStudy.title}`,
            },
          ],
        }}
      />
      <Layout>
        <Box h={['104px', null, '70px']} bg="black" mt={['-104px', null, '-70px']} />
        <Container>
          <Fragment>
            <CaseStudyHeader
              mb={[10, null, 12, 20]}
              title={caseStudy.title}
              featuredImage={caseStudy.largeFeaturedImageUrl}
            />
            <Box mx="auto" maxW={[null, null, '512px', '790px']}>
              {caseStudy.content && <ArticleMarkdown source={caseStudy.content} escapeHtml={false} />}
              <Divider mt={[8, null, null, 10]} mb={[6, null, null, 9]} />
              <ArticleSocialShare />
            </Box>
          </Fragment>
        </Container>
        <GradientBanner
          title={t('caseStudies.bannerTitle')}
          subtitle={t('caseStudies.bannerSubtitle')}
          action={
            <Link href="/contact-us/support" passHref legacyBehavior>
              <Button colorScheme="black" as="a">
                {t('caseStudies.buttonText')}
                <Icon ml={2} as={ArrowIcon} boxSize={5} />
              </Button>
            </Link>
          }
        />
      </Layout>
    </Fragment>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const store = generateStore();

  const caseStudies = await fetchAllCaseStudies(store);

  const paths = caseStudies.map((caseStudy) => ({
    params: { id: caseStudy.linkSlug },
  }));

  return {
    paths,
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const store = generateStore();

  const { id: caseStudyId } = splitIdAndSlug(params.id as string);

  const caseStudyResponse = await fetchCaseStudy(store, caseStudyId);

  const rawCaseStudy = (caseStudyResponse as any).__response.data;

  if (!rawCaseStudy) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      rawCaseStudy,
    },
    revalidate: 60,
  };
};

export default CaseStudyPage;
